import React, { useState } from "react";
import Tooltip from "./Tooltip";
import "./Calendar.css";

interface SelectedDate {
  date: string;
  direction: string | number;
}

interface CalendarProps {
  selectedDates: SelectedDate[];
  onDateSelect: (date: string) => void;
  onDateRemove: (date: string) => void;
  dateSectionSow?: boolean;
  /** Функция, возвращающая контент для тултипа по дате. Если не передана, тултип не отображается. */
  getTooltipContent?: (date: string) => React.ReactNode;
}

// Генерация цвета на основе значения (string | number)
// Для маленьких чисел (1,2,3...) цвета сильно отличаются благодаря золотому углу
const generateColor = (value: string | number): string => {
  let numericValue: number;
  if (typeof value === "number") {
    numericValue = value;
  } else {
    // Пытаемся преобразовать строку в число, если возможно
    const parsed = Number(value);
    if (!isNaN(parsed) && isFinite(parsed)) {
      numericValue = parsed;
    } else {
      // Если строка не число, используем хеш строки
      let hash = 0;
      for (let i = 0; i < value.length; i++) {
        hash = value.charCodeAt(i) + ((hash << 5) - hash);
      }
      numericValue = Math.abs(hash);
    }
  }
  // Золотой угол (в градусах) обеспечивает максимальное расстояние между последовательными значениями
  const goldenAngle = 137.508;
  const hue = (numericValue * goldenAngle) % 360;
  // Более сочные и насыщенные цвета
  return `hsl(${hue}, 85%, 70%)`;
};

const Calendar: React.FC<CalendarProps> = ({
  selectedDates,
  onDateSelect,
  onDateRemove,
  dateSectionSow = true,
  getTooltipContent,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const navigateMonth = (direction: number) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + direction,
      1,
    );
    setCurrentDate(newDate);
  };

  const generateCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Первый день месяца
    const firstDay = new Date(year, month, 1);
    // Последний день месяца
    const lastDay = new Date(year, month + 1, 0);

    // Первый день недели месяца (0 = воскресенье, 1 = понедельник, и т.д.)
    const firstDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Преобразуем воскресенье в 6, а остальные сдвигаем

    // Дни недели
    const weekdays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

    // Дни месяца
    const days = [];

    // Добавляем дни предыдущего месяца
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const dateStr = `${new Date(year, month - 1, prevMonthLastDay - i).getFullYear()}-${String(new Date(year, month - 1, prevMonthLastDay - i).getMonth() + 1).padStart(2, "0")}-${String(prevMonthLastDay - i).padStart(2, "0")}`;
      days.push({ date: dateStr, isCurrentMonth: false });
    }

    // Добавляем дни текущего месяца
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
      days.push({ date: dateStr, isCurrentMonth: true });
    }

    // Добавляем дни следующего месяца
    const remainingDays = 42 - days.length; // 6 строк * 7 дней
    for (let i = 1; i <= remainingDays; i++) {
      const dateStr = `${new Date(year, month + 1, i).getFullYear()}-${String(new Date(year, month + 1, i).getMonth() + 1).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
      days.push({ date: dateStr, isCurrentMonth: false });
    }

    return { weekdays, days };
  };

  const { weekdays, days } = generateCalendar();

  // Создаем Map для быстрого доступа к direction по дате
  const selectedDatesMap = new Map<string, string | number>();
  selectedDates.forEach((item) => {
    selectedDatesMap.set(item.date, item.direction);
  });

  const handleDateClick = (date: string) => {
    onDateSelect(date);
  };

  const handleRemoveDate = (date: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onDateRemove(date);
  };

  return (
    <div>
      <div className="calendar">
        <div className="calendar-header">
          <div className="calendar-title">
            {currentDate.toLocaleDateString("ru-RU", {
              month: "long",
              year: "numeric",
            })}
          </div>
          <div className="calendar-nav">
            <button type="button" onClick={() => navigateMonth(-1)}>
              ←
            </button>
            <button type="button" onClick={() => setCurrentDate(new Date())}>
              Сегодня
            </button>
            <button type="button" onClick={() => navigateMonth(1)}>
              →
            </button>
          </div>
        </div>
        <div className="weekdays">
          {weekdays.map((day, index) => (
            <div key={index} className="weekday">
              {day}
            </div>
          ))}
        </div>
        <div className="days">
          {days.map((dayObj, index) => {
            const day = new Date(dayObj.date).getDate();
            const direction = selectedDatesMap.get(dayObj.date);
            // console.log(selectedDatesMap);
            const isSelected = direction !== undefined;
            const backgroundColor = isSelected
              ? generateColor(direction)
              : undefined;
            const dayElement = (
              <div
                key={index}
                className={`day ${isSelected ? "selected" : ""} ${!dayObj.isCurrentMonth ? "other-month" : ""}`}
                onClick={() => handleDateClick(dayObj.date)}
                style={backgroundColor ? { backgroundColor } : undefined}
              >
                {day}
              </div>
            );

            // Если есть функция для контента тултипа, оборачиваем в Tooltip
            if (getTooltipContent && getTooltipContent(dayObj.date)) {
              const tooltipContent = getTooltipContent(dayObj.date);
              if (tooltipContent) {
                return (
                  <Tooltip
                    key={index}
                    content={tooltipContent}
                    position="top"
                    delay={300}
                    maxWidth={250}
                  >
                    {dayElement}
                  </Tooltip>
                );
              }
            }

            return dayElement;
          })}
        </div>
      </div>
      {dateSectionSow && <h3>Выбранные даты</h3>}
      {dateSectionSow && (
        <div className="selected-dates">
          {selectedDates.length === 0 ? (
            <p>Нет выбранных дат</p>
          ) : (
            selectedDates.map((item, index) => (
              <div
                key={index}
                className="selected-date"
                onClick={(e) => handleRemoveDate(item.date, e)}
              >
                {new Date(item.date).toLocaleDateString("ru-RU")}
                <span className="remove">✕</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Calendar;
